import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAddCustomTokenPair, useGetSupportedTokens, useGetCustomTokenPairs } from '../hooks/useQueries';
import { toast } from 'sonner';

// Helper to safely extract error messages - prevents binary data from being displayed
function getSafeErrorMessage(error: any, fallback: string = 'An error occurred'): string {
  if (!error) return fallback;
  
  // If error.message exists and is a reasonable string, use it
  if (error.message && typeof error.message === 'string') {
    // Check if it looks like binary data (long string of numbers/commas)
    if (error.message.length > 200 || /^[\d,\s\[\]"]+$/.test(error.message)) {
      return fallback;
    }
    return error.message;
  }
  
  // If error is a string itself
  if (typeof error === 'string') {
    if (error.length > 200 || /^[\d,\s\[\]"]+$/.test(error)) {
      return fallback;
    }
    return error;
  }
  
  return fallback;
}

interface AddCustomTokenModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

// Network options for dropdown
const NETWORK_OPTIONS = [
    { value: 'ethereum', label: 'Ethereum' },
    { value: 'base', label: 'Base' },
    { value: 'bsc', label: 'Binance Smart Chain' },
    { value: 'hyperliquid', label: 'Hyperliquid' },
    { value: 'ravencoin', label: 'Ravencoin' },
    { value: 'bitcoin', label: 'Bitcoin' },
    { value: 'litecoin', label: 'Litecoin' },
    { value: 'dogecoin', label: 'Dogecoin' },
    { value: 'bitcoin cash', label: 'Bitcoin Cash' },
    { value: 'zcash', label: 'Zcash' },
    { value: 'dash', label: 'Dash' },
    { value: 'komodo', label: 'Komodo' },
    { value: 'qtum', label: 'Qtum' },
    { value: 'solana', label: 'Solana' },
    { value: 'xrp', label: 'XRP Ledger' },
    { value: 'monero', label: 'Monero' },
    { value: 'cardano', label: 'Cardano' },
    { value: 'icp', label: 'Internet Computer' },
];

// Validate contract address format based on network
function validateContractAddress(address: string, network: string): { valid: boolean; error?: string } {
    if (!address) return { valid: true }; // Optional field
    
    const networkLower = network.toLowerCase();
    
    // EVM chains: 0x followed by 40 hex characters
    const evmChains = ['ethereum', 'eth', 'mainnet', 'base', 'bsc', 'binance', 'binance smart chain', 'hyperliquid'];
    if (evmChains.includes(networkLower)) {
        if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
            return { valid: false, error: 'Invalid EVM address format. Must be 0x followed by 40 hex characters.' };
        }
    }
    
    // UTXO chains: No contract addresses
    const utxoChains = ['ravencoin', 'rvn', 'bitcoin', 'btc', 'litecoin', 'ltc', 'dogecoin', 'doge', 'bitcoin cash', 'bch', 'zcash', 'zec', 'dash', 'komodo', 'kmd', 'qtum', 'kaspa', 'kas'];
    if (utxoChains.includes(networkLower)) {
        return { valid: false, error: 'UTXO chains do not use contract addresses. Leave this field empty.' };
    }
    
    return { valid: true };
}

export default function AddCustomTokenModal({ open, onOpenChange }: AddCustomTokenModalProps) {
    const [symbol, setSymbol] = useState('');
    const [name, setName] = useState('');
    const [contractAddress, setContractAddress] = useState('');
    const [network, setNetwork] = useState('');
    const [addressError, setAddressError] = useState('');
    const [duplicateError, setDuplicateError] = useState('');
    const { mutate: addCustomTokenPair, isPending } = useAddCustomTokenPair();
    const { data: backendTokens } = useGetSupportedTokens();
    const { data: customTokenPairs } = useGetCustomTokenPairs();
    
    // Check for duplicate symbols (memoized to avoid stale closures)
    const isDuplicate = useCallback((symbol: string): boolean => {
        const upperSymbol = symbol.toUpperCase();
        
        // Check backend tokens
        if (backendTokens?.some(([s]) => s.toUpperCase() === upperSymbol)) {
            return true;
        }
        
        // Check custom tokens
        if (customTokenPairs?.some(t => t.symbol.toUpperCase() === upperSymbol)) {
            return true;
        }
        
        return false;
    }, [backendTokens, customTokenPairs]);
    
    // Validate address when contract address or network changes
    useEffect(() => {
        if (contractAddress && network) {
            const validation = validateContractAddress(contractAddress, network);
            if (!validation.valid) {
                setAddressError(validation.error || 'Invalid address format');
            } else {
                setAddressError('');
            }
        } else {
            setAddressError('');
        }
    }, [contractAddress, network]);
    
    // Check for duplicates when symbol changes
    useEffect(() => {
        if (symbol.trim()) {
            if (isDuplicate(symbol.trim())) {
                setDuplicateError('This token symbol already exists');
            } else {
                setDuplicateError('');
            }
        } else {
            setDuplicateError('');
        }
    }, [symbol, isDuplicate]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!symbol.trim() || !name.trim()) {
            toast.error('Symbol and name are required');
            return;
        }
        
        // Require network selection
        if (!network.trim()) {
            toast.error('Network selection is required');
            return;
        }
        
        // Check for duplicates
        if (isDuplicate(symbol.trim())) {
            toast.error('Token symbol already exists');
            return;
        }
        
        // Validate address if provided
        if (contractAddress && network) {
            const validation = validateContractAddress(contractAddress, network);
            if (!validation.valid) {
                toast.error(validation.error || 'Invalid contract address format');
                return;
            }
        }
        
        // Require contract address for EVM chains
        const networkLower = network.toLowerCase();
        const evmChains = ['ethereum', 'eth', 'mainnet', 'base', 'bsc', 'binance', 'binance smart chain', 'hyperliquid'];
        if (evmChains.includes(networkLower) && !contractAddress.trim()) {
            toast.error('Contract address is required for EVM chains');
            return;
        }

        addCustomTokenPair(
            {
                symbol: symbol.trim().toUpperCase(),
                name: name.trim(),
                contractAddress: contractAddress.trim() || undefined,
                network: network.trim() || undefined,
            },
            {
                onSuccess: () => {
                    toast.success(`Custom token pair ${symbol.toUpperCase()} added successfully`);
                    setSymbol('');
                    setName('');
                    setContractAddress('');
                    setNetwork('');
                    setAddressError('');
                    setDuplicateError('');
                    onOpenChange(false);
                },
                onError: (error: any) => {
                    toast.error(getSafeErrorMessage(error, 'Failed to add custom token pair'));
                },
            }
        );
    };

    const handleClose = () => {
        if (!isPending) {
            setSymbol('');
            setName('');
            setContractAddress('');
            setNetwork('');
            setAddressError('');
            setDuplicateError('');
            onOpenChange(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Add Custom Token Pair</DialogTitle>
                    <DialogDescription>
                        Add a custom token to your swap interface. Symbol and name are required.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="symbol">Token Symbol *</Label>
                        <Input
                            id="symbol"
                            placeholder="e.g., BTC"
                            value={symbol}
                            onChange={(e) => setSymbol(e.target.value)}
                            disabled={isPending}
                            required
                            className={duplicateError ? 'border-destructive' : ''}
                        />
                        {duplicateError && (
                            <div className="flex items-center gap-1 text-sm text-destructive">
                                <AlertCircle className="h-3 w-3" />
                                <span>{duplicateError}</span>
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="name">Token Name *</Label>
                        <Input
                            id="name"
                            placeholder="e.g., Bitcoin"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={isPending}
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="network">Network *</Label>
                        <Select value={network} onValueChange={setNetwork} disabled={isPending} required>
                            <SelectTrigger>
                                <SelectValue placeholder="Select network" />
                            </SelectTrigger>
                            <SelectContent>
                                {NETWORK_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="contractAddress">
                            Contract Address {network && ['ethereum', 'eth', 'mainnet', 'base', 'bsc', 'binance', 'binance smart chain', 'hyperliquid'].includes(network.toLowerCase()) ? '*' : '(Optional)'}
                        </Label>
                        <Input
                            id="contractAddress"
                            placeholder="e.g., 0x..."
                            value={contractAddress}
                            onChange={(e) => setContractAddress(e.target.value)}
                            disabled={isPending}
                            className={addressError ? 'border-destructive' : ''}
                        />
                        {addressError && (
                            <div className="flex items-center gap-1 text-sm text-destructive">
                                <AlertCircle className="h-3 w-3" />
                                <span>{addressError}</span>
                            </div>
                        )}
                        {network && ['ravencoin', 'rvn', 'bitcoin', 'btc', 'litecoin', 'ltc', 'dogecoin', 'doge', 'bitcoin cash', 'bch', 'zcash', 'zec', 'dash', 'komodo', 'kmd', 'qtum'].includes(network.toLowerCase()) && (
                            <p className="text-xs text-muted-foreground">
                                UTXO chains don't use contract addresses. Leave this field empty.
                            </p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClose}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending}>
                            {isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Adding...
                                </>
                            ) : (
                                'Add Token'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
